import React, { useState } from "react";
import clsx from "clsx";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import { Typography } from "@material-ui/core";
import Collapse from "@material-ui/core/Collapse";
import EditableTableCustomers from "./EditableTableCustomers";
import parseData from "../../../../../../components/texts/parseData";
import {useRequirementTableStyles} from "../../../../../../common/styles/CustomerRequirementStyles";

const useStyles = useRequirementTableStyles;

const tableHeadings = [
    { name: "Volume", size: "12.5%", alignRight: "true" },
    { name: "FAME", size: "12.5%", alignRight: "true" },
    { name: "FAME Adv.", size: "12.5%", alignRight: "true" },
    { name: "HVO/HRO", size: "12.5%", alignRight: "true" },
    { name: "HVO/HRO Adv.", size: "12.5%", alignRight: "true" },
    { name: "Required Total", size: "12.5%", alignRight: "true" },
    { name: "Allocated Total", size: "12.5%", alignRight: "true" },
    { name: "Remaining Total", size: "12.5%", alignRight: "true" },
];

interface Props {
    stepData: any;
    requirements: any[];
    available_quantities: any;
    customer: { id: number; index: number };
    allocateUpdateCustomerReq: any;
}

const OffRoadCustomerReqTable: React.FC<Props> = ({
                                               stepData,
                                               requirements,
                                               available_quantities,
                                               customer,
                                               allocateUpdateCustomerReq,
                                           }) => {
    const [showAllocation, setShowAllocation] = useState<any>({
        index: null,
        type_id: null,
    });
    const classes = useStyles();

    const toggleShowAllocation = (index: number, type_id: string) =>
        setShowAllocation(
            showAllocation.index === index && showAllocation.type_id === type_id
                ? {
                    index: null,
                    type_id: null,
                }
                : { index: index, type_id: type_id }
        );

    const getSumAllocated = (cust_id: any, req_id: any, type: string): number => {
        return (
            stepData.sumAllocated[cust_id] &&
            stepData.sumAllocated[cust_id][req_id] &&
            stepData.sumAllocated[cust_id][req_id][type]
        );
    };

    const getAdjustedVolumeStatus = (cust_id: any, requirement: any, fuel_type: any, isVolume: boolean): any => {
        let status = "";
        let volume = 0;
        if (fuel_type === "fame" || fuel_type === "fame_advanced") {
            let sumFame = getSumAllocated(cust_id, requirement.id, "fame");
            let sumFameAdvanced = getSumAllocated(cust_id, requirement.id, "fame_advanced");
            let extraFame = sumFame - requirement.required_emission_reduction_fame;
            let extraFameAdvanced = sumFameAdvanced - requirement.required_emission_reduction_fame_advanced;

            if (fuel_type === "fame") {
                volume = sumFame;
            } else if (fuel_type === "fame_advanced") {
                volume = sumFameAdvanced;
            }

            if (extraFame < 0 && extraFameAdvanced > 0 &&
                (sumFame + extraFameAdvanced >= requirement.required_emission_reduction_fame)) {
                const surplusFame = Math.min(Math.abs(extraFame), extraFameAdvanced);
                if (fuel_type === "fame") {
                    volume = sumFame + surplusFame;
                    status = "Covered by Surplus " + surplusFame + " from FAME advanced";
                } else if (fuel_type === "fame_advanced") {
                    volume = sumFameAdvanced - surplusFame;
                }
            }

            if (extraFameAdvanced < 0 && extraFame > 0 &&
                (sumFameAdvanced + extraFame >= requirement.required_emission_reduction_fame_advanced)) {
                const surplusFameAdv = Math.min(Math.abs(extraFameAdvanced), extraFame);
                if (fuel_type === "fame_advanced") {
                    volume = sumFameAdvanced + surplusFameAdv;
                    status = "Covered by Surplus " + surplusFameAdv + " from FAME";
                } else if (fuel_type === "fame") {
                    volume = sumFame - surplusFameAdv;
                }
            }

            if (isVolume) {
                return volume;
            }
        } else {
            let sumHvoHro = getSumAllocated(cust_id, requirement.id, "hvo_hro");
            let sumHvoHroAdvanced = getSumAllocated(cust_id, requirement.id, "hvo_hro_advanced");
            let extraHvoHro = sumHvoHro - requirement.required_emission_reduction_hvo_hro;
            let extraHvoHroAdvanced = sumHvoHroAdvanced - requirement.required_emission_reduction_hvo_hro_advanced;

            if (fuel_type === "hvo_hro") {
                volume = sumHvoHro;
            } else if (fuel_type === "hvo_hro_advanced") {
                volume = sumHvoHroAdvanced;
            }

            if (extraHvoHro < 0 && extraHvoHroAdvanced > 0 &&
                (sumHvoHro + extraHvoHroAdvanced >= requirement.required_emission_reduction_hvo_hro)) {
                const surplusHvo = Math.min(Math.abs(extraHvoHro), extraHvoHroAdvanced);
                if (fuel_type === "hvo_hro") {
                    volume = sumHvoHro + surplusHvo;
                    status = "Covered by Surplus " + surplusHvo + " from HVO/HRO advanced";
                } else if (fuel_type === "hvo_hro_advanced") {
                    volume = sumHvoHroAdvanced - surplusHvo;
                }
            }
            if (extraHvoHroAdvanced < 0 && extraHvoHro > 0 &&
                (sumHvoHroAdvanced + extraHvoHro >= requirement.required_emission_reduction_hvo_hro_advanced)) {
                const surplusHvoAdv = Math.min(Math.abs(extraHvoHroAdvanced), extraHvoHro);
                if (fuel_type === "hvo_hro_advanced") {
                    volume = sumHvoHroAdvanced + surplusHvoAdv;
                    status = "Covered by Surplus " + surplusHvoAdv + " from HVO/HRO";
                } else if (fuel_type === "hvo_hro") {
                    volume = sumHvoHro - surplusHvoAdv;
                }
            }

            if (isVolume) {
                return volume;
            }
        }
        return status;
    };

    const getStatus = (cust_id: any, requirement: any): string => {
        let sumFame = getSumAllocated(cust_id, requirement.id, "fame");
        let sumFameAdvanced = getSumAllocated(cust_id, requirement.id, "fame_advanced");
        let sumHvoHro = getSumAllocated(cust_id, requirement.id, "hvo_hro");
        let sumHvoHroAdvanced = getSumAllocated(cust_id, requirement.id, "hvo_hro_advanced");

        if (
            sumFame < requirement.required_emission_reduction_fame ||
            sumFameAdvanced < requirement.required_emission_reduction_fame_advanced ||
            sumHvoHro < requirement.required_emission_reduction_hvo_hro ||
            sumHvoHroAdvanced < requirement.required_emission_reduction_hvo_hro_advanced
        )
            return "Not Complete";
        if (
            sumFame > requirement.required_emission_reduction_fame ||
            sumFameAdvanced > requirement.required_emission_reduction_fame_advanced ||
            sumHvoHro > requirement.required_emission_reduction_hvo_hro ||
            sumHvoHroAdvanced > requirement.required_emission_reduction_hvo_hro_advanced
        )
            return "Overallocated";

        return "Complete";
    };

    const showAllocationButton = (
        index: number,
        type_name: string,
        type_id: string
    ) => {
        return (
            <Button
                variant="contained"
                color="primary"
                onClick={() => toggleShowAllocation(index, type_id)}
                className={classes.btnAllocation}
            >
                {showAllocation.index === index && showAllocation.type_id === type_id
                    ? "Hide Allocation " + type_name
                    : "View Allocation " + type_name}
            </Button>
        );
    };

    return (
        <>
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>
                        {tableHeadings.map((heading, hidx) => (
                            <TableCell
                                align={heading.alignRight ? "right" : "left"}
                                style={{ width: heading.size }}
                                key={heading.name + "_heading"}
                            >
                                {heading.name}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                {requirements.map((requirement: any, index: number) => (
                    <TableBody key={"customer" + customer.id + requirement.id}>
                        <TableRow>
                            <TableCell colSpan={10} className={classes.tableRowTitle}>
                                <div className={classes.flex}>
                                    <Typography
                                        variant="body2"
                                        color="primary"
                                        className={classes.rowTitle}
                                    >
                                        {requirement.product_name}
                                    </Typography>
                                    <div>
                                        {showAllocationButton(index, "FAME", "fame")}
                                        {showAllocationButton(index, "FAME Adv.", "fame_advanced")}
                                        {showAllocationButton(index, "HVO/HRO", "hvo_hro")}
                                        {showAllocationButton(index, "HVO/HRO Adv.", "hvo_hro_advanced")}
                                    </div>
                                </div>
                            </TableCell>
                        </TableRow>

                        <TableRow
                            className={clsx({
                                [classes.green]:
                                getStatus(customer.id, requirement) === "Complete",
                                [classes.red]:
                                getStatus(customer.id, requirement) === "Not Complete",
                                [classes.yellow]:
                                getStatus(customer.id, requirement) === "Overallocated",
                            })}
                        >
                            <TableCell align="right">
                                {parseData(requirement.volume, 2)}
                            </TableCell>
                            <TableCell align="right">
                                {parseData(
                                        getSumAllocated(customer.id, requirement.id, "fame"),
                                        2
                                    ) +
                                    " / " +
                                    parseData(requirement.required_emission_reduction_fame, 2)}
                            </TableCell>
                            <TableCell align="right">
                                {parseData(
                                        getSumAllocated(customer.id, requirement.id, "fame_advanced"),
                                        2
                                    ) +
                                    " / " +
                                    parseData(requirement.required_emission_reduction_fame_advanced, 2)}
                            </TableCell>
                            <TableCell align="right">
                                {parseData(
                                        getSumAllocated(customer.id, requirement.id, "hvo_hro"),
                                        2
                                    ) +
                                    " / " +
                                    parseData(requirement.required_emission_reduction_hvo_hro, 2)}
                            </TableCell>
                            <TableCell align="right">
                                {parseData(
                                        getSumAllocated(customer.id, requirement.id, "hvo_hro_advanced"),
                                        2
                                    ) +
                                    " / " +
                                    parseData(requirement.required_emission_reduction_hvo_hro_advanced, 2)}
                            </TableCell>
                            <TableCell align="right">
                                {parseData(requirement.emission_reduction_total, 2)}
                            </TableCell>
                            <TableCell align="right">
                                {parseData(
                                    getSumAllocated(customer.id, requirement.id, "total"),
                                    2
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {parseData(
                                    (
                                        requirement.emission_reduction_total -
                                        getSumAllocated(customer.id, requirement.id, "total")
                                    ).toFixed(2),
                                    2
                                )}
                            </TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell
                                colSpan={10}
                                className={classes.collapseRow}
                                style={{ padding: 0 }}
                            >
                                <Collapse in={showAllocation.index === index}>
                                    <EditableTableCustomers
                                        stepData={stepData}
                                        data={requirement["biotemplates_" + showAllocation.type_id]}
                                        type={showAllocation.type_id}
                                        available_quantities={available_quantities}
                                        customer={customer}
                                        requirement={{
                                            id: requirement.id,
                                            index: index,
                                            type: showAllocation.type_id,
                                        }}
                                        allocateUpdate={allocateUpdateCustomerReq}
                                    />
                                </Collapse>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                ))}
            </Table>
        </>
    );
};

export default OffRoadCustomerReqTable;
